/**
 * FieldSyncIndicator Component
 * Visual indicator for field-level sync status in data entry forms
 * 
 * States:
 * - pristine: No indicator (field empty/unchanged)
 * - modified: Yellow dot (user typing, not yet saved)
 * - saving: Animated spinner (saving to IndexedDB)
 * - local: Orange cloud icon (saved locally, pending sync)
 * - syncing: Animated cloud (syncing to server)
 * - synced: Blue checkmark (on server, not submitted)
 * - submitted: Green checkmark (officially submitted)
 * - error: Red X with retry option
 */

import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  CheckCircle2,
  CloudOff,
  Cloud,
  RefreshCw,
  AlertCircle,
  Loader2,
  Circle
} from 'lucide-react';
import { cn } from '../ui/utils';
import type { FieldSyncStatus } from '../../types/draftEntry';

interface FieldSyncIndicatorProps {
  status: FieldSyncStatus;
  onRetry?: () => void;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

const STATUS_CONFIG: Record<FieldSyncStatus, {
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  label: string;
  tooltip: string;
}> = {
  pristine: {
    icon: null,
    color: 'text-slate-400',
    bgColor: 'bg-transparent',
    label: '',
    tooltip: 'Not modified',
  },
  modified: {
    icon: <Circle className="w-full h-full fill-current" />,
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10',
    label: 'Editing',
    tooltip: 'Unsaved changes',
  },
  saving: {
    icon: <Loader2 className="w-full h-full animate-spin" />,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    label: 'Saving...',
    tooltip: 'Saving locally...',
  },
  local: {
    icon: <CloudOff className="w-full h-full" />,
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
    label: 'Local',
    tooltip: 'Saved locally, pending sync',
  },
  syncing: {
    icon: <Cloud className="w-full h-full animate-pulse" />,
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10',
    label: 'Syncing...',
    tooltip: 'Syncing to server...',
  },
  synced: {
    icon: <Check className="w-full h-full" />,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    label: 'Synced',
    tooltip: 'Synced to server',
  },
  submitted: {
    icon: <CheckCircle2 className="w-full h-full" />,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    label: 'Submitted',
    tooltip: 'Officially submitted',
  },
  error: {
    icon: <AlertCircle className="w-full h-full" />,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10',
    label: 'Error',
    tooltip: 'Sync failed - tap to retry',
  },
};

const SIZE_CONFIG = {
  sm: {
    iconSize: 'w-3 h-3',
    padding: 'p-0.5',
    labelSize: 'text-[9px]',
    gap: 'gap-0.5',
  },
  md: {
    iconSize: 'w-4 h-4',
    padding: 'p-1',
    labelSize: 'text-[10px]',
    gap: 'gap-1',
  },
  lg: {
    iconSize: 'w-5 h-5',
    padding: 'p-1.5',
    labelSize: 'text-xs',
    gap: 'gap-1.5',
  },
};

export function FieldSyncIndicator({
  status,
  onRetry,
  size = 'md',
  showLabel = false,
  className,
}: FieldSyncIndicatorProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  // Hook must be called before any early returns to follow Rules of Hooks
  const handleClick = useCallback(() => {
    if (status === 'error' && onRetry) {
      onRetry();
    }
  }, [status, onRetry]);

  const config = STATUS_CONFIG[status];
  const sizeConfig = SIZE_CONFIG[size];

  // Don't render anything for pristine status
  if (status === 'pristine') {
    return null;
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={status}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.15 }}
        className={cn(
          "relative flex items-center",
          sizeConfig.gap,
          className
        )}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={handleClick}
        role={status === 'error' ? 'button' : undefined}
        tabIndex={status === 'error' ? 0 : undefined}
      >
        {/* Icon */}
        <div
          className={cn(
            "flex items-center justify-center rounded-full transition-colors",
            config.color,
            config.bgColor,
            sizeConfig.iconSize,
            sizeConfig.padding,
            status === 'error' && 'cursor-pointer hover:bg-red-500/20'
          )}
        >
          {config.icon}
        </div>

        {/* Label */}
        {showLabel && config.label && (
          <span className={cn(
            "font-medium uppercase tracking-wide",
            config.color,
            sizeConfig.labelSize
          )}>
            {config.label}
          </span>
        )}

        {/* Retry icon for error state */}
        {status === 'error' && onRetry && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={onRetry}
            className={cn(
              "flex items-center justify-center rounded-full",
              "bg-red-500/20 hover:bg-red-500/30 text-red-500",
              sizeConfig.iconSize,
              sizeConfig.padding
            )}
          >
            <RefreshCw className="w-full h-full" />
          </motion.button>
        )}

        {/* Tooltip */}
        <AnimatePresence>
          {showTooltip && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 5 }}
              className={cn(
                "absolute z-50 px-2 py-1 text-xs rounded-md whitespace-nowrap",
                "bg-slate-900 text-white dark:bg-slate-700",
                "top-full mt-1 left-1/2 -translate-x-1/2"
              )}
            >
              {config.tooltip}
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 dark:bg-slate-700 rotate-45" />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Compact sync status badge for form field labels
 */
interface SyncBadgeProps {
  status: FieldSyncStatus;
  className?: string;
}

export function SyncBadge({ status, className }: SyncBadgeProps) {
  const config = STATUS_CONFIG[status];

  if (status === 'pristine') return null;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-medium uppercase",
        config.color,
        config.bgColor,
        className
      )}
    >
      <span className="w-2.5 h-2.5">{config.icon}</span>
      {config.label}
    </span>
  );
}

/**
 * Entry-level submission status indicator
 */
interface SubmissionStatusProps {
  isSubmitted: boolean;
  isConfirmed: boolean;
  pendingSync: number;
  totalFields: number;
  className?: string;
}

export function SubmissionStatus({
  isSubmitted,
  isConfirmed,
  pendingSync,
  totalFields,
  className,
}: SubmissionStatusProps) {
  // Calculate sync percentage
  const syncedCount = totalFields - pendingSync;
  const syncPercentage = totalFields > 0 ? Math.round((syncedCount / totalFields) * 100) : 0;

  if (isSubmitted && isConfirmed) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-xl",
        "bg-emerald-500/10 border border-emerald-500/30",
        className
      )}>
        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">
            Submitted & Confirmed
          </span>
          <span className="text-[10px] text-emerald-500/80">
            Entry is locked. Request manager approval to edit.
          </span>
        </div>
      </div>
    );
  }

  if (isSubmitted && !isConfirmed) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-xl",
        "bg-blue-500/10 border border-blue-500/30",
        className
      )}>
        <Cloud className="w-5 h-5 text-blue-500 animate-pulse" />
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
            Submitted - Syncing...
          </span>
          <span className="text-[10px] text-blue-500/80">
            Waiting for server confirmation
          </span>
        </div>
      </div>
    );
  }

  if (pendingSync > 0) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-xl",
        "bg-orange-500/10 border border-orange-500/30",
        className
      )}>
        <CloudOff className="w-5 h-5 text-orange-500" />
        <div className="flex flex-col flex-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
              {pendingSync} field{pendingSync > 1 ? 's' : ''} pending sync
            </span>
            <span className="text-[10px] font-mono text-orange-500">
              {syncPercentage}%
            </span>
          </div>
          <div className="w-full h-1 bg-orange-200 dark:bg-orange-900/50 rounded-full mt-1 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${syncPercentage}%` }}
              className="h-full bg-orange-500 rounded-full"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(
      "flex items-center gap-2 px-3 py-2 rounded-xl",
      "bg-blue-500/10 border border-blue-500/30",
      className
    )}>
      <Check className="w-5 h-5 text-blue-500" />
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
          All fields synced
        </span>
        <span className="text-[10px] text-blue-500/80">
          Ready to submit
        </span>
      </div>
    </div>
  );
}

export default FieldSyncIndicator;
